import SelectInput, {
    SelectOption,
} from '@components/common/SelectInput/SelectInput';
import DatePicker from '@components/common/DatePicker';
import TextInput from '@components/common/TextInput';
import { farmingHist, FarmlandAddReq } from '@/api/growth/farmland/type';
import { useState } from 'react';
import { formatDateString, parseDate } from '@utils/Utils';

interface EnvironmentMngProps {
    formData: FarmlandAddReq;
    onInputChange: (field: string, value: farmingHist[]) => void;
}

// 환경조건별 수치 매핑
const environmentConditions = {
    투명비닐: { erad: '0', emax: '2.5', emin: '2.5', ewind: '-99' },
    흑색멀칭: { erad: '-0.1', emax: '1.5', emin: '1.5', ewind: '-99' },
    백색멀칭: { erad: '0.1', emax: '-0.5', emin: '-1.0', ewind: '-99' },
    부직포: { erad: '-0.05', emax: '1.0', emin: '1.0', ewind: '-99' },
    터널: { erad: '0', emax: '4.0', emin: '3.0', ewind: '-99' },
    차광망: { erad: '-0.3', emax: '-99', emin: '-99', ewind: '-99' },
    방풍망: { erad: '0', emax: '-99', emin: '-99', ewind: '0.7' },
};

const EnvironmentMng = ({ formData, onInputChange }: EnvironmentMngProps) => {
    const [farmingHists, setFarmingHists] = useState<farmingHist[]>(
        formData.farmingHistList?.filter(h => h.category === 'E') || [
            {
                category: 'E',
                workDt: '',
                iamt: '',
                iame: '',
                chcod: '',
                chamt: '',
                chme: '',
                erad: '-99',
                emax: '-99',
                emin: '-99',
                ewind: '-99',
                ename: '',
                fmcd: '',
                facd: '',
                famn: '',
                famp: '',
                famk: '',
                rcod: '',
                ramt: '',
                rdep: '',
                timpl: '',
                tdep: '',
            },
        ],
    );

    const handleFarmingHistChange = (
        index: number,
        field: keyof farmingHist,
        value: string | number,
    ) => {
        const updated = [...farmingHists];
        updated[index] = { ...updated[index], [field]: value };

        // 환경조건 선택 시 자동으로 수치 설정
        if (field === 'ename' && typeof value === 'string') {
            const condition =
                environmentConditions[
                    value as keyof typeof environmentConditions
                ];
            if (condition) {
                updated[index] = {
                    ...updated[index],
                    erad: condition.erad,
                    emax: condition.emax,
                    emin: condition.emin,
                    ewind: condition.ewind,
                };
            }
        }

        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const updateParentFarmingHistList = (environmentData: farmingHist[]) => {
        const otherData =
            formData.farmingHistList?.filter(h => h.category !== 'E') || [];
        const allData = [...otherData, ...environmentData];
        onInputChange('farmingHistList', allData);
    };

    const addFarmingHist = () => {
        const newRecord: farmingHist = {
            category: 'E',
            workDt: '',
            iamt: '',
            iame: '',
            chcod: '',
            chamt: '',
            chme: '',
            erad: '-99',
            emax: '-99',
            emin: '-99',
            ewind: '-99',
            ename: '',
            fmcd: '',
            facd: '',
            famn: '',
            famp: '',
            famk: '',
            rcod: '',
            ramt: '',
            rdep: '',
            timpl: '',
            tdep: '',
        };
        const updated = [...farmingHists, newRecord];
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const removeFarmingHist = (index: number) => {
        const updated = farmingHists.filter((_, i) => i !== index);
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const environmentOptions: SelectOption[] = [
        { value: '투명비닐', label: '투명비닐 - 햇빛을 투과시키며 보온함' },
        {
            value: '흑색멀칭',
            label: '흑색멀칭 - 잡초를 억제하고 지온을 상승시킴',
        },
        {
            value: '백색멀칭',
            label: '백색멀칭 - 지온 상승을 억제하고 햇빛을 반사함',
        },
        { value: '부직포', label: '부직포 - 통기성을 유지하며 보온함' },
        { value: '터널', label: '터널 - 터널형 구조로 보온 및 보습함' },
        { value: '차광망', label: '차광망 - 강한 햇빛을 차단함' },
        { value: '방풍망', label: '방풍망 - 외부 바람을 차단함' },
    ];

    return (
        <div className="environment-management">
            <div className="row mb-3">
                <div className="col-12 text-end">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addFarmingHist}
                    >
                        <i className="ri-add-line me-1"></i>
                        재배환경 추가
                    </button>
                </div>
            </div>

            {farmingHists.length > 0 && (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-3">
                        <thead className="text-center">
                            <tr>
                                <th style={{ width: '12%' }}>일자 선택</th>
                                <th style={{ width: '20%' }}>환경조건</th>
                                <th style={{ width: '7%' }}>
                                    일사량
                                    <div className="fs-9">(ERAD)</div>
                                </th>
                                <th style={{ width: '7%' }}>
                                    최고기온
                                    <div className="fs-9">(℃)</div>
                                </th>
                                <th style={{ width: '7%' }}>
                                    최저기온
                                    <div className="fs-9">(℃)</div>
                                </th>
                                <th style={{ width: '7%' }}>
                                    풍속
                                    <div className="fs-9">(m/s)</div>
                                </th>
                                <th style={{ width: '5%' }}>삭제</th>
                            </tr>
                        </thead>
                        <tbody>
                            {farmingHists.map((record, index) => (
                                <tr key={index}>
                                    <td>
                                        <DatePicker
                                            selectedDate={parseDate(
                                                record.workDt || '',
                                            )}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'workDt',
                                                    formatDateString(value),
                                                )
                                            }
                                            size="sm"
                                            placeholder="날짜를 선택해주세요"
                                            maxDate={undefined}
                                            showYearDropdown={false}
                                            showMonthDropdown={false}
                                        />
                                    </td>
                                    <td>
                                        <SelectInput
                                            options={environmentOptions}
                                            value={record.ename || ''}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'ename',
                                                    value,
                                                )
                                            }
                                            placeholder="환경조건 선택"
                                            selectClassName="form-control"
                                        />
                                    </td>
                                    <td>
                                        <TextInput
                                            value={
                                                record.erad
                                                    ? record.erad.toString()
                                                    : ''
                                            }
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'erad',
                                                    value,
                                                )
                                            }
                                            placeholder="일사량"
                                            disabled={true}
                                        />
                                    </td>
                                    <td>
                                        <TextInput
                                            value={
                                                record.emax
                                                    ? record.emax.toString()
                                                    : ''
                                            }
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'emax',
                                                    value,
                                                )
                                            }
                                            placeholder="최고기온"
                                            disabled={true}
                                        />
                                    </td>
                                    <td>
                                        <TextInput
                                            value={
                                                record.emin
                                                    ? record.emin.toString()
                                                    : ''
                                            }
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'emin',
                                                    value,
                                                )
                                            }
                                            placeholder="최저기온"
                                            disabled={true}
                                        />
                                    </td>
                                    <td>
                                        <TextInput
                                            value={
                                                record.ewind
                                                    ? record.ewind.toString()
                                                    : ''
                                            }
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'ewind',
                                                    value,
                                                )
                                            }
                                            placeholder="풍속"
                                            disabled={true}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={() =>
                                                removeFarmingHist(index)
                                            }
                                            title="삭제"
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {farmingHists.length === 0 && (
                <div className="alert alert-info text-center py-4">
                    <p className="mb-0">
                        <i className="ri-information-line me-2"></i>
                        재배환경(E) 관련 내용을 입력해주세요. 추가 버튼을 눌러
                        재배환경 기록을 추가할 수 있습니다.
                    </p>
                </div>
            )}

            <div className="row mb-3">
                <div className="col-12 text-center">
                    <div className="alert alert-info mb-3" role="alert">
                        <i className="ri-information-line me-2"></i>
                        재배환경 조건을 선택하면 일사량, 최고기온, 최저기온,
                        풍속 값이 자동으로 설정됩니다.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnvironmentMng;
